import { NextRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import { getAuthenticatedUser } from "../api/user";
import { userRedirects } from "../redirects/userRedirects";
import { getAuthenticatedUserQuerie, Token } from "../types/queries";
import { Errors } from "../types/api";
import { AxiosError } from "axios";

// Status codes that will trigger a cookie clear
const clearTokenStates = [Errors.AccessDenied, Errors.NotFound];

/**
 * This method gets the authenticated user on the react component lifecycle and checks for redirects
 * @param token the auth token
 * @param router next router object
 * @param skipRedirect wether to check for redirect or not
 */
export const getAuthenticatedUserClient = (token: Token, router: NextRouter, skipRedirect = false) => {
    // Get the user
    const authenticatedUser = useQuery({
        queryKey: getAuthenticatedUserQuerie(token),
        queryFn: () => getAuthenticatedUser(token),
        throwOnError: false,
        meta: { logNotFound: true }
    });

    const { data, isFetched, error } = authenticatedUser;

    let busy = authenticatedUser.isFetching;

    // Important: Only allow the redirect mechanism to kick in for certain status codes
    // the redirect system will take care of clearing the token and redirecting the user
    if (error && !clearTokenStates.includes((error as AxiosError)?.response?.status as number)) {
        throw Error(`Unable to get user`);
    }

    // If this url is protected and we have fetched the user, ran validation checks
    if (!skipRedirect && isFetched) {
        busy = userRedirects(data, undefined, router) || false;
    }

    return { authenticatedUser, busy };
};
